import { matchPath } from 'react-router-dom';
import { Module, Page } from '../interfaces/Config';

export interface MenuTab {
  // Route the tab navigates to (and the Tabs value)
  route: string;
  // All routes that should highlight this tab, including those of deduplicated pages
  routes: string[];
  page: Page;
}

// Pages rendering the same component with the same caption are treated as one menu entry
const getMenuTabKey = (page: Page): string => `${page.path}|${page.menuCaptionId ?? page.menuCaption}`;

/**
 * Collects the menu pages of the given modules into top bar tabs.
 * The modules' main pages (`default: true`) come first, followed by the pages extended into the modules.
 * Extended pages shared between modules (e.g. Live Weather configured for both MooringAnalysis and YardSafe)
 * are shown once, using the last module's page and route. The other routes are kept in `routes`
 * so they still resolve to the shared tab.
 * @param modules The authorised modules.
 * @returns The deduplicated menu tabs.
 */
const getMenuTabs = (modules: Module[]): MenuTab[] => {
  const mainTabs: MenuTab[] = [];
  const extendedTabs = new Map<string, MenuTab>();

  (modules ?? []).forEach((module) => {
    module.pages.forEach((page) => {
      if (!(page.visibility?.indexOf('menu') > -1)) return;

      const route = module.route + page.route;

      if (page.default) {
        mainTabs.push({ route, routes: [route], page });

        return;
      }

      const key = getMenuTabKey(page);
      const routes = [...(extendedTabs.get(key)?.routes ?? []), route];

      extendedTabs.set(key, { route, routes, page });
    });
  });

  return [...mainTabs, ...extendedTabs.values()];
};

/**
 * Finds the tab to highlight for the given pathname.
 * Matches exactly, like the page routes in `routes.tsx`.
 * @param tabs The menu tabs.
 * @param pathname The current location pathname.
 * @returns The matching tab, or undefined if none match.
 */
export const getActiveMenuTab = (tabs: MenuTab[], pathname: string): MenuTab =>
  tabs.find((tab) => tab.routes.some((route) => matchPath(pathname, { path: route, exact: true })));

export default getMenuTabs;
